import { useState } from "react";
import { MoreVertical, Download, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { ExportCandidatesModal } from "@/pages/candidates/components/ExportCandidatesModal";
import { useDuplicateCandidate } from "@/pages/candidates/hooks/useCandidate";
import type { ProfileDetail } from "@/interface/profile";
import { cn } from "@/lib/utils";

interface CandidateActionsDropdownProps {
  profile?: ProfileDetail | null;
  triggerClassName?: string;
  align?: "start" | "center" | "end";
}

export function CandidateActionsDropdown({
  profile,
  triggerClassName,
  align = "end",
}: CandidateActionsDropdownProps) {
  const duplicateCandidate = useDuplicateCandidate();
  const [isDuplicateConfirmOpen, setIsDuplicateConfirmOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const fullName = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
    profile.username
    : "this candidate";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="outline"
              className={cn(
                "bg-[#1a0b2e]/50 border-purple-500/30 text-white hover:bg-purple-500/20 cursor-pointer",
                triggerClassName,
              )}
            />
          }
        >
          More Actions <MoreVertical className="h-4 w-4 ml-2" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-52 p-2">
          {profile?.is_original && (
            <DropdownMenuItem
              onClick={() => setIsDuplicateConfirmOpen(true)}
              className="cursor-pointer flex items-center p-2"
            >
              <Copy className="h-4 w-4 mr-2 text-slate-400" />
              Duplicate Profile
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => setIsExportModalOpen(true)}
            className="cursor-pointer flex items-center p-2"
          >
            <Download className="h-4 w-4 mr-2 text-slate-400" />
            Export
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ExportCandidatesModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        selectedIds={profile?.id ? [profile.id] : []}
        filename={`${profile.first_name || ""} ${profile.last_name || ""}`.split(" ").join("_")}
      />

      <ConfirmationDialog
        isOpen={isDuplicateConfirmOpen}
        onClose={() => setIsDuplicateConfirmOpen(false)}
        title="Duplicate Profile"
        description={`Are you sure you want to duplicate ${fullName}'s profile?`}
        confirmText="Duplicate"
        cancelText="Cancel"
        confirmVariant="primary"
        isPending={duplicateCandidate.isPending}
        onConfirm={() => {
          if (!profile?.id) return;
          duplicateCandidate.mutate(profile.id, {
            onSuccess: () => {
              // toast.success("Profile duplicated successfully");
              setIsDuplicateConfirmOpen(false);
            },
            onError: (error: Error) => {
              toast.error(error?.message || "Failed to duplicate profile");
              setIsDuplicateConfirmOpen(false);
            },
          });
        }}
      />
    </>
  );
}
